import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { DataService } from '@app/shared/services/data-service';
import { OshDTO } from '@app/shared/services/dtos/osh/osh.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { CreateOrEditOshAnalysisDialogData, Osh } from '@app/shared/types/models/osh/osh.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '@env/environment';
import { finalize, Observable, tap } from 'rxjs';
import { OshAnalysisFactory } from './factories/osh-analysis.factory';

@Injectable({ providedIn: 'root' })
export class ProjectOshAnalysisDataService extends DataService<OshDTO, Osh> {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly oshAnalysisFactory: OshAnalysisFactory = inject(OshAnalysisFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private oshAnalyses: DataStore<Osh> = new DataStore<Osh>();
  private projectOshAnalyses: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public getProjectOshAnalyses(projectId: string): Signal<Osh[] | undefined> {
    if (!projectId) {
      return signal(undefined);
    }

    if (!this.projectOshAnalyses.hasDataForId(projectId)) {
      this.fetchProjectOshAnalyses(projectId);
    }

    return computed(
      () =>
        this.projectOshAnalyses
          .get(projectId)()
          ?.ids?.map(id => this.oshAnalyses.get(id)()) || []
    );
  }

  public getById(analysisId: string): Signal<Osh | undefined> {
    return computed(() => this.oshAnalyses.get(analysisId)());
  }

  public loadProjectOshAnalyses(projectId: string, force = false): void {
    if (!force && this.projectOshAnalyses.hasDataForId(projectId)) {
      return;
    }
    this.fetchProjectOshAnalyses(projectId);
  }

  public upsertLocalData(dto: OshDTO): Signal<Osh> {
    return dto ? this.oshAnalyses.upsert(this.oshAnalysisFactory.produce(dto)) : signal(undefined);
  }

  protected fetchProjectOshAnalyses(projectId: string): void {
    // this.loaderService.startLoading();

    this.httpClient.get<OshDTO[]>(environment.APIEndPoint + `projects/${projectId}/osh`).subscribe({
      next: (projectOshAnalyses: OshDTO[]) => {
        this.oshAnalyses.upsertMany(projectOshAnalyses.map(itm => this.oshAnalysisFactory.produce(itm)));
        this.projectOshAnalyses.upsert({
          id: projectId,
          ids: projectOshAnalyses.map(itm => itm.id)
        });
      }
      // error: () => this.loaderService.stopLoading(),
      // complete: () => this.loaderService.stopLoading()
    });
  }

  public deleteOshAnalysis(projectId: string, oshId: string): Observable<void> {
    this.loaderService.startLoading();

    return this.httpClient.delete<void>(`${environment.APIEndPoint}projects/${projectId}/osh/${oshId}`).pipe(
      tap(() => {
        this.toastService.success(this.translateService.instant('osh.toasts.successfully-deleted'));
        const existing = this.projectOshAnalyses.get(projectId)()?.ids ?? [];
        this.projectOshAnalyses.upsert({
          id: projectId,
          ids: existing.filter(id => id !== oshId)
        });
        this.oshAnalyses.delete(oshId);
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public addProjectOshAnalysis(projectId: string, osh: CreateOrEditOshAnalysisDialogData): Observable<OshDTO> {
    this.loaderService.startLoading();

    return this.httpClient.post<OshDTO>(`${environment.APIEndPoint}projects/${projectId}/osh/`, osh).pipe(
      tap(dto => {
        this.upsertLocalData(dto);
        this.toastService.success(this.translateService.instant('osh.toasts.successfully-added'));
        const existing = this.projectOshAnalyses.get(projectId)()?.ids ?? [];
        this.projectOshAnalyses.upsert({
          id: projectId,
          ids: [...existing, dto.id]
        });
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public editProjectOshAnalysis(projectId: string, osh: CreateOrEditOshAnalysisDialogData): Observable<OshDTO> {
    this.loaderService.startLoading();

    return this.httpClient.put<OshDTO>(`${environment.APIEndPoint}projects/${projectId}/osh/${osh.id}`, osh).pipe(
      tap(dto => {
        this.upsertLocalData(dto);
        this.toastService.success(this.translateService.instant('osh.toasts.successfully-updated'));
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }
}
